export type CsvColumn = { accessor: string; label: string };

export function deriveCsvColumns<T extends Record<string, unknown>>(rows: T[]): CsvColumn[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
  }

  return keys.map((key) => ({ accessor: key, label: key }));
}

const FORMULA_START = /^[=+@\t\r]/;
// Negative numbers as exports write them: `-5`, `-.5`, `-1,234`, `-1e3`.
const PLAIN_NUMBER = /^-?[\d.,]+(e[+-]?\d+)?$/i;

/**
 * A string a spreadsheet would run as a formula (it starts with `=`, `+`, `@`, a tab, a carriage return, or a `-`
 * that starts neither a plain number nor is the bare `-` placeholder), prefixed with a single quote so it reads as
 * text. Other strings unchanged.
 */
export function neutraliseFormula(value: string): string {
  const formula =
    FORMULA_START.test(value) || (value.startsWith('-') && value !== '-' && !PLAIN_NUMBER.test(value));
  return formula ? `'${value}` : value;
}

export function escapeCsvValue(value: unknown): string {
  if (typeof value === 'string') {
    return `"${neutraliseFormula(value).replace(/"/g, '""')}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value.toString();
  }
  if (value == null) {
    return '';
  }
  return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
}

export function serializeCsv<T extends Record<string, unknown>>(
  data: T[],
  columns: CsvColumn[],
): string {
  return [
    columns.map((column) => escapeCsvValue(column.label)).join(','),
    ...data.map((row) =>
      columns.map((column) => escapeCsvValue(row[column.accessor])).join(','),
    ),
  ].join('\n');
}
